import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { BsFillExclamationTriangleFill } from 'react-icons/bs';
import { toast } from 'react-toastify';
import checkIconFilled from 'v2//icons/checkIconFilled.svg';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import crossIconFilled from 'v2/icons/crossIconFilledRed.svg';
import { ProposalEntity, User } from 'v2/models';
import { isProposalAssignedToMe } from 'v2/utils/proposalUtils';
import restFactory from 'v2/utils/restFactory';
import RequestAssignModal from '../RequestAssignModal';
import classnames from 'classnames';
import styles from './waitingSellerActions.module.scss';
import NomadGuidance from '../../NomadGuidance/NomadGuidance';
import { ProposalRejectionByProviderModal } from './components';
import AssignToMeToggle from './components/AssignToMe/AssignToMeToggle';
import SectionHeader from 'v2/common/DRM/SectionHeader/SectionHeader';
import AssignToMeAlert from './components/AssignToMe/AssignToMeAlert';
import { useDispatch } from 'react-redux';
import { TAppDispatch as Dispatch } from 'store';
import { fetchAllProposals } from 'actions';

export interface WaitingSellerActionsProps {
  proposal: ProposalEntity;
  onRespondProposal: (show?: boolean) => void;
  user: User;
  detailsFormatTitle?: string;
}

export const WaitingSellerActions = ({
  proposal,
  onRespondProposal,
  user,
  detailsFormatTitle,
}: WaitingSellerActionsProps) => {
  const dispatch = useDispatch<Dispatch>();
  const isDetailsFormat = Boolean(detailsFormatTitle);
  const [isBusy, setIsBusy] = useState(false);
  const [proposalAssigedToMe, setProposalAssigedToMe] = useState(
    isProposalAssignedToMe(proposal, user)
  );
  const [showModalType, setShowModalType] = useState<
    'ACCEPT' | 'DECLINE' | false
  >(false);
  const [showReqAssignModal, setShowReqAssignModal] = useState<boolean>(false);
  // Internal proposal state to update when user toggles the assigned_to button
  // and don't wait until BE returns a response, this state will be automatically updated
  // in a useEffect hook when BE returns the response to sync this state with what is in the DB
  const [proposalInternal, setproposalInternal] =
    useState<ProposalEntity>(proposal);
  const [alreadyTaken, setAlreadyTaken] = useState<boolean>(false);
  const [
    showProposalRejectionByProviderModal,
    setShowProposalRejectionByProviderModal,
  ] = useState(false);

  const showActionButtons = [
    !proposalAssigedToMe,
    proposalInternal.assigned_to,
  ].every(Boolean);

  const showAssignToMeToogle =
    Boolean(proposalInternal.data_provider.multiple_active_users) &&
    [!proposalInternal.assigned_to, proposalAssigedToMe].some(Boolean);

  const showAssignToMeAlert = [
    proposalInternal.assigned_to,
    !proposalAssigedToMe,
  ].every(Boolean);
  useEffect(() => {
    setProposalAssigedToMe(isProposalAssignedToMe(proposal, user));
    setproposalInternal(proposal);
  }, [proposal, user]);

  const toggleAssignedToMe = async (isAssignedToMe: boolean) => {
    let notShow = false;
    try {
      setIsBusy(true);
      setProposalAssigedToMe(!isAssignedToMe);
      setproposalInternal({
        ...proposalInternal,
        assigned_to: !isAssignedToMe
          ? {
              username: user.username,
              first_name: user.first_name,
              last_name: user.last_name,
            }
          : null,
      });
      await restFactory.patch<ProposalEntity['assigned_to']>(
        `/dj/api/v1/proposal/${proposalInternal.id}/`,
        {
          assigned_to: !isAssignedToMe ? user.id : null,
        }
      );
    } catch (error) {
      setAlreadyTaken(true);
      notShow = true;
    } finally {
      await dispatch(fetchAllProposals(false));
      setIsBusy(false);
    }
    return notShow;
  };

  const acceptProposal = () => {
    if (
      [
        proposalInternal.data_provider.multiple_active_users,
        !proposalAssigedToMe,
        !proposalInternal.assigned_to,
      ].every(Boolean)
    ) {
      setShowReqAssignModal(true);
      return;
    }
    if (showActionButtons) {
      setShowModalType('ACCEPT');
      return;
    }
    onRespondProposal();
  };

  const handleDismissSearch = () => {
    if (showActionButtons) {
      setShowModalType('DECLINE');
      return;
    }
    setShowProposalRejectionByProviderModal(true);
  };

  const showConfirmModal = () => {
    return showModalType ? (
      <ConfirmationModal
        classes={{ header: 'bg-light' }}
        title={
          <div className="d-flex align-items-center">
            <BsFillExclamationTriangleFill className="mr-3 text-primary" />
            <span>Request already assigned!</span>
          </div>
        }
        onOk={() => {
          const responseAction =
            showModalType === 'ACCEPT'
              ? Promise.resolve(onRespondProposal())
              : setShowProposalRejectionByProviderModal(true);
          setShowModalType(false);
          return responseAction;
        }}
        onCancel={() => setShowModalType(false)}
        show={true}
        okBtnText="Proceed"
      >
        <p>
          A colleague of yours has indicated they are working on a response to
          this request. Would you like to{' '}
          {showModalType === 'ACCEPT' ? 'respond' : 'decline'} anyway?
        </p>
      </ConfirmationModal>
    ) : null;
  };

  const showRequestAssignmentModal = () => {
    return showReqAssignModal ? (
      <RequestAssignModal
        user={user}
        onOk={(show: boolean) => {
          setShowReqAssignModal(false);
          onRespondProposal(show);
        }}
        onCancel={() => setShowReqAssignModal(false)}
        toggleAssignedToMe={toggleAssignedToMe}
      />
    ) : null;
  };

  if (
    [alreadyTaken, proposalInternal.assigned_to, !proposalAssigedToMe].every(
      Boolean
    )
  ) {
    toast.error(
      `This proposal has already been assigned to ${proposalInternal.assigned_to?.username}`
    );
    setAlreadyTaken(false);
  }
  return (
    <>
      {[isDetailsFormat, !showAssignToMeAlert].every(Boolean) && (
        <div className="d-flex justify-content-between">
          {
            <SectionHeader
              title={detailsFormatTitle || ''}
              className={styles.header}
              titleClassName={styles.title}
            />
          }
          <div className={styles.formatDetailsToggle}>
            <AssignToMeToggle
              isChecked={proposalAssigedToMe}
              isDisabled={isBusy}
              onChange={() => toggleAssignedToMe(proposalAssigedToMe)}
              show={showAssignToMeToogle}
            />
          </div>
        </div>
      )}
      {showAssignToMeAlert && (
        <AssignToMeAlert
          proposal={proposalInternal}
          isDetailsFormat={isDetailsFormat}
        />
      )}

      <NomadGuidance proposal={proposal} />
      <div
        className={classnames(
          isDetailsFormat && !showAssignToMeAlert
            ? styles.buttonsDetailsFormat
            : 'd-flex flex-column flex-lg-row mt-3'
        )}
      >
        {![isDetailsFormat, showAssignToMeAlert].every(Boolean) && (
          <Button
            variant="danger"
            className={classnames(
              'mr-3 mt-3 mb-3 my-lg-0',
              styles.dismissSearchBtn
            )}
            onClick={handleDismissSearch}
          >
            <img
              className="mr-2"
              src={crossIconFilled}
              alt="rejectProposalIcon"
            ></img>
            Not a match
          </Button>
        )}
        <Button
          variant="secondary"
          className={styles.acceptSearchBtn}
          onClick={acceptProposal}
          data-cy="proposal-item-accept-button"
        >
          <img
            className="mr-2"
            src={checkIconFilled}
            alt="startConversationIcon"
          ></img>
          I have that data!
        </Button>

        {!isDetailsFormat && (
          <AssignToMeToggle
            isChecked={proposalAssigedToMe}
            isDisabled={isBusy}
            onChange={() => toggleAssignedToMe(proposalAssigedToMe)}
            show={showAssignToMeToogle}
          />
        )}
      </div>
      {showConfirmModal()}
      {showRequestAssignmentModal()}

      <ProposalRejectionByProviderModal
        proposal={proposal}
        show={showProposalRejectionByProviderModal}
        onClose={() => setShowProposalRejectionByProviderModal(false)}
      />
    </>
  );
};
